import { useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpCircleIcon,
  ChatBubbleOvalLeftIcon,
  CheckCircleIcon,
  EllipsisHorizontalIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { HandRaisedIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import type { Prayer } from '@flock-types/prayer.types';
import { isPrayerLifted } from '@flock-types/prayer.types';
import { formatMessageTime } from '@/lib/chatTime';
import { timeAgo } from '@/lib/timeAgo';
import EncouragementSection from './EncouragementSection';

function toDate(value: Prayer['createdAt']): Date {
  if (typeof value === 'string') return new Date(value);
  if (value && typeof value === 'object' && 'toDate' in value) return (value as any).toDate();
  return new Date(value as any);
}

interface Props {
  prayer: Prayer;
  /** PG user id of the signed-in user. */
  currentUserId: string | undefined;
  isLeader: boolean;
  /** Combined views: which group (and org, when ambiguous) this prayer is from. */
  context?: { groupName: string; organizationName?: string };
  onPray: (prayer: Prayer) => void;
  onEncourage: (prayer: Prayer, note: string) => Promise<void>;
  onReactionToggle: (prayer: Prayer, noteId: string, emoji: string, reacted: boolean) => void;
  onMarkAnswered: (prayer: Prayer, answerText: string) => Promise<void>;
  onLiftToggle: (prayer: Prayer) => void;
  onEdit: (prayer: Prayer, newText: string) => Promise<void>;
  onDelete: (prayer: Prayer) => void;
}

export default function PrayerCard({
  prayer,
  currentUserId,
  isLeader,
  context,
  onPray,
  onEncourage,
  onReactionToggle,
  onMarkAnswered,
  onLiftToggle,
  onEdit,
  onDelete,
}: Props) {
  const [encOpen, setEncOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(prayer.prayerText);
  const [answering, setAnswering] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [busy, setBusy] = useState(false);

  const own = currentUserId === prayer.userId;
  const hasPrayed = Boolean(currentUserId && prayer.prayedForBy.includes(currentUserId));
  const lifted = isPrayerLifted(prayer);
  const answered = prayer.status === 'answered';
  const isPraise = prayer.type === 'praise';
  const showLiftedTheme = lifted && !answered;

  return (
    <div
      className={clsx(
        'relative rounded-2xl border p-4 transition',
        showLiftedTheme
          ? '-translate-y-0.5 border-amber-600/[0.18] bg-gradient-to-br from-[#fffbeb] to-[#fef3c7] shadow-[0_4px_12px_rgba(217,119,6,0.12)]'
          : 'border-flock-gray-200 bg-white shadow-sm'
      )}
    >
      {/* Amber shimmer sweep (Expo parity). Clipped in its own rounded layer so
          the card itself never needs overflow-hidden (menus would clip). */}
      {showLiftedTheme && (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
          <div className="absolute inset-y-0 left-0 w-3/5 animate-prayer-shimmer bg-[linear-gradient(90deg,transparent,rgba(245,158,11,0.08)_30%,rgba(245,158,11,0.15)_50%,rgba(245,158,11,0.08)_70%,transparent)] motion-reduce:animate-none" />
        </div>
      )}

      <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={clsx(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                isPraise ? 'bg-amber-100 text-amber-700' : 'bg-flock-navy-100 text-flock-navy-600'
              )}
            >
              {isPraise ? 'Praise' : 'Prayer'}
            </span>
            {answered && (
              <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
                <CheckCircleIcon className="h-3 w-3" /> Answered
              </span>
            )}
            {showLiftedTheme && (
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                Lifted
              </span>
            )}
            {prayer.privacyLevel !== 'public' && (
              <span
                className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-flock-navy-400"
                title={prayer.privacyLevel === 'leaders_only' ? 'Visible to leaders only' : 'Private'}
              >
                <LockClosedIcon className="h-3 w-3" />
                {prayer.privacyLevel === 'leaders_only' ? 'Leaders' : 'Private'}
              </span>
            )}
            {context && (
              <span className="rounded-full bg-flock-navy-50 px-2 py-0.5 text-[10px] font-medium text-flock-navy-500">
                {context.groupName}
                {context.organizationName ? ` · ${context.organizationName}` : ''}
              </span>
            )}
          </div>

          {(own || isLeader) && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="rounded p-1 text-flock-navy-300 hover:bg-flock-navy-50 hover:text-flock-navy-600"
              >
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-7 z-20 w-44 overflow-hidden rounded-xl border border-flock-gray-200 bg-white py-1 shadow-xl">
                  {own && !answered && (
                    <>
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          setEditing(true);
                          setEditText(prayer.prayerText);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-flock-navy-700 hover:bg-flock-navy-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          setAnswering(true);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-flock-navy-700 hover:bg-flock-navy-50"
                      >
                        Mark as answered
                      </button>
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          onLiftToggle(prayer);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-flock-navy-700 hover:bg-flock-navy-50"
                      >
                        {lifted ? 'Remove lift' : 'Lift this prayer'}
                      </button>
                    </>
                  )}
                  {(own || isLeader) && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete(prayer);
                      }}
                      className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-2 text-xs text-flock-navy-400">
          {prayer.userName} · {formatMessageTime(toDate(prayer.createdAt))}
        </div>

        {editing ? (
          <div className="mt-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={3}
              maxLength={1000}
              className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
            />
            <div
              className={
                editText.length >= 1000
                  ? 'text-right text-xs text-red-600'
                  : 'text-right text-xs text-flock-navy-400'
              }
            >
              {editText.length} / 1000
            </div>
            <div className="mt-2 flex gap-2">
              <button
                disabled={busy || !editText.trim()}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await onEdit(prayer, editText.trim());
                    setEditing(false);
                  } finally {
                    setBusy(false);
                  }
                }}
                className="rounded-lg bg-flock-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-flock-navy-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="rounded-lg border border-flock-gray-200 px-3 py-1.5 text-xs text-flock-navy-600"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-2 whitespace-pre-wrap text-sm text-flock-navy-900">{prayer.prayerText}</p>
        )}

        {answered && prayer.answerText && (
          <div className="mt-2 rounded-lg border-l-4 border-green-400 bg-green-50 px-3 py-2 text-sm text-green-800">
            <span className="font-semibold">How God answered: </span>
            {prayer.answerText}
          </div>
        )}

        {answering && (
          <div className="mt-2">
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={2}
              placeholder="How was this prayer answered? (optional)"
              className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
            />
            <div className="mt-2 flex gap-2">
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await onMarkAnswered(prayer, answerText.trim());
                    setAnswering(false);
                  } finally {
                    setBusy(false);
                  }
                }}
                className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                Mark Answered
              </button>
              <button
                onClick={() => setAnswering(false)}
                className="rounded-lg border border-flock-gray-200 px-3 py-1.5 text-xs text-flock-navy-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => !hasPrayed && onPray(prayer)}
            disabled={hasPrayed}
            className={clsx(
              'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition',
              hasPrayed
                ? 'border-flock-navy-400 bg-flock-navy-100 text-flock-navy-700'
                : 'border-flock-gray-200 text-flock-navy-600 hover:bg-flock-navy-50'
            )}
          >
            <HandRaisedIcon className="h-4 w-4" />
            {hasPrayed ? 'Prayed' : 'I Prayed'}
            {prayer.prayedForCount > 0 && <span>· {prayer.prayedForCount}</span>}
          </button>

          <button
            onClick={() => setEncOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-full border border-flock-gray-200 px-3 py-1.5 text-xs font-medium text-flock-navy-600 transition hover:bg-flock-navy-50"
          >
            <ChatBubbleOvalLeftIcon className="h-4 w-4" />
            Encourage
            {prayer.encouragementNotes.length > 0 && <span>· {prayer.encouragementNotes.length}</span>}
          </button>

          {own && !answered && !lifted && (
            <button
              onClick={() => onLiftToggle(prayer)}
              title="Bring this prayer back to the top for 48 hours"
              className="flex items-center gap-1.5 rounded-full border border-flock-gray-200 px-3 py-1.5 text-xs font-medium text-flock-navy-600 transition hover:bg-flock-navy-50"
            >
              <ArrowUpCircleIcon className="h-4 w-4" />
              Lift
            </button>
          )}

          {own && !answered && lifted && (
            <button
              onClick={() => onLiftToggle(prayer)}
              title="Remove the lift from this prayer"
              className="flex items-center gap-1 rounded-full bg-amber-700/80 px-2.5 py-1.5 text-[11px] font-bold text-white transition hover:bg-amber-700"
            >
              <ArrowDownIcon className="h-3.5 w-3.5" />
              Unlift
            </button>
          )}
        </div>

        {/* Candle footer — who still needs prayer (Expo parity) */}
        {showLiftedTheme && (
          <div className="mt-2.5 flex items-center gap-1.5 border-t border-[rgba(217,119,6,0.12)] pt-2.5">
            <span className="text-sm">🕯️</span>
            <span className="text-xs text-[#92400e]">
              {(prayer.userName || 'They').split(' ')[0]} still needs prayer for this
              {prayer.liftedAt
                ? (() => {
                    const ago = timeAgo(toDate(prayer.liftedAt!));
                    return ` · ${ago === 'just now' ? ago : `${ago} ago`}`;
                  })()
                : ''}
            </span>
          </div>
        )}

        <EncouragementSection
          prayer={prayer}
          expanded={encOpen}
          onExpandChange={setEncOpen}
          onSubmit={(note) => onEncourage(prayer, note)}
          onReactionToggle={(noteId, emoji, reacted) => onReactionToggle(prayer, noteId, emoji, reacted)}
        />
      </div>
    </div>
  );
}
